// Composes a purpose-specific detail line for a wallet Transaction row, using
// fields already on the record (platform/orderDate/remark for purchase & refund)
// plus fields added server-side by backend/utils/transactionEnrichment.js
// (paymentSource/stationName for deposit, itemCount/trackingNumber for payment).
// `t` (react-i18next) is optional — when omitted, falls back to plain English so
// this can be reused from screens that don't yet localize this row (e.g. the
// customer portal wallet history).

const PLATFORM_LABELS = { Lazada: "Lazada", Shopee: "Shopee", Tiktok: "Tiktok", Shein: "Shein", Other: "Other" };
const PAYMENT_SOURCE_KEYS = {
  bank_transfer: "wallet.admin.paymentSourceBankTransfer",
  cash_store: "wallet.admin.paymentSourceCashStore",
  staff_advance: "wallet.admin.paymentSourceStaffAdvance",
};
const BOILERPLATE_DESCRIPTION = {
  purchase: "Purchase order recorded by staff",
  refund: "Refund recorded by staff",
};

function formatDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function extraDescription(purpose, description) {
  if (!description || description === BOILERPLATE_DESCRIPTION[purpose]) return null;
  return description;
}

export function formatPaymentSource(paymentSource, t) {
  if (!paymentSource) return "";
  const key = PAYMENT_SOURCE_KEYS[paymentSource];
  return t && key ? t(key) : paymentSource.replace(/_/g, " ");
}

export function formatWalletTxnDetail(txn, t) {
  const parts = [];

  switch (txn.purpose) {
    case "purchase": {
      if (txn.platform) {
        const label =
          txn.platform === "Other" && txn.platformOther
            ? txn.platformOther
            : t
            ? t(`wallet.admin.platforms.${txn.platform}`)
            : PLATFORM_LABELS[txn.platform] || txn.platform;
        parts.push(label);
      }
      parts.push(formatDate(txn.orderDate));
      parts.push(extraDescription("purchase", txn.description));
      break;
    }
    case "refund": {
      parts.push(txn.remark);
      parts.push(extraDescription("refund", txn.description));
      break;
    }
    case "deposit": {
      parts.push(formatPaymentSource(txn.paymentSource, t));
      parts.push(txn.stationName);
      parts.push(txn.remark);
      break;
    }
    case "payment": {
      if (typeof txn.itemCount === "number") {
        parts.push(
          t
            ? t("wallet.admin.itemsCount", { count: txn.itemCount })
            : `${txn.itemCount} item${txn.itemCount === 1 ? "" : "s"}`
        );
      }
      if (txn.itemCount === 1 && txn.trackingNumber) parts.push(txn.trackingNumber);
      break;
    }
    default:
      parts.push(txn.description);
  }

  const joined = parts.filter(Boolean).join(" · ");
  return joined || txn.description || "";
}
